/*
 OK FBI — Public Cinematic Homepage
 Route: /  (public — no auth required)
 Design goal: feel like entering a classified investigation HQ, not a SaaS landing page.
 - Black + Antique Gold identity (no neon, no bright yellow).
 - RTL Arabic-first typography.
 - Scroll storytelling: 9 acts from entry → final CTA.
 - Motion: cinematic, slow, respects prefers-reduced-motion.
 - Demo case is fully fictional. NO prices. NO fake stats.
*/
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useAuth } from "@/pages/AuthPage";
import { api } from "@/lib/api";
import { assetUrl } from "@/contexts/SessionContext";
import { PublicFooter } from "@/pages/LegalPages";
import { usePageContent } from "@/lib/pageContent";
import { PrimitiveBlockList as _PrimitiveBlockList } from "@/blocks/PrimitiveBlocks";
import { useEditorBridge } from "@/hooks/useEditorBridge";
import {
  Fingerprint,
  FileText,
  ScanLine,
  Radar,
  Award,
  Users,
  ArrowLeft,
  ChevronDown,
  Circle,
  MapPin,
  Camera,
  Clock,
} from "lucide-react";

// Built-in OK FBI case covers are already served as public assets by the backend.
// These are the ONLY case media used on the public homepage — no other case
// content (evidence photos, suspect portraits, solution reveals) is exposed.
// Server-side rule: covers live under /generated_images/case-*-cover.png which
// is the public static route; nothing else from a case is referenced here.
const PUBLIC_COVERS = [
  "/generated_images/case-001-cover.png",
  "/generated_images/case-002-cover.png",
  "/generated_images/case-003-cover.png",
  "/generated_images/case-004-cover.png",
  "/generated_images/case-005-cover.png",
];
const publicMedia = (i) => assetUrl(PUBLIC_COVERS[i % PUBLIC_COVERS.length]);

// ══════════════════════════════════════════════════════════════
//  1) SHARED ATMOSPHERE — grain, dust, light sweep primitives
// ══════════════════════════════════════════════════════════════

/** Very subtle film-grain overlay. Fixed to viewport. */
export function FilmGrain() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[1] mix-blend-overlay opacity-[0.08]"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/></svg>\")",
      }}
    />
  );
}

/** Slow drifting dust motes across the whole viewport. */
export function Dust({ count = 22 }) {
  const reduced = useReducedMotion();
  const dots = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: 1 + Math.random() * 1.4,
        dur: 30 + Math.random() * 40,
        delay: -Math.random() * 30,
      })),
    [count],
  );
  if (reduced) return null;
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-[1] overflow-hidden">
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-[#C5A059]"
          style={{
            top: `${d.top}%`,
            left: `${d.left}%`,
            width: d.size,
            height: d.size,
            opacity: 0.14,
            animation: `okfbi-drift ${d.dur}s linear ${d.delay}s infinite`,
            filter: "blur(0.3px)",
          }}
        />
      ))}
      <style>{`
        @keyframes okfbi-drift {
          0%   { transform: translate3d(0,0,0); opacity: 0.05; }
          25%  { transform: translate3d(6px,-12px,0); opacity: 0.16; }
          50%  { transform: translate3d(-4px,-24px,0); opacity: 0.10; }
          75%  { transform: translate3d(2px,-36px,0); opacity: 0.14; }
          100% { transform: translate3d(0,-48px,0); opacity: 0.05; }
        }
      `}</style>
    </div>
  );
}

/** Slow, single-pass metallic light sweep on any container (adds .okfbi-sheen wrapper). */
const SHEEN_KEYFRAMES = `
  @keyframes okfbi-sheen {
    0%   { transform: translateX(-120%) skewX(-18deg); opacity: 0; }
    12%  { opacity: 0.9; }
    50%  { opacity: 0.5; }
    100% { transform: translateX(220%) skewX(-18deg); opacity: 0; }
  }
`;

// ══════════════════════════════════════════════════════════════
//  2) OPENING SEQUENCE — once per session
// ══════════════════════════════════════════════════════════════
function OpeningSequence({ onDone }) {
  const reduced = useReducedMotion();
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (reduced) { onDone(); return; }
    const t1 = setTimeout(() => setStep(1), 350);
    const t2 = setTimeout(() => setStep(2), 900);
    const t3 = setTimeout(() => setStep(3), 1500);
    const t4 = setTimeout(() => onDone(), 2000);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [reduced, onDone]);

  return (
    <motion.div
      className="fixed inset-0 z-[80] bg-black flex items-center justify-center"
      initial={{ opacity: 1 }}
      animate={{ opacity: step >= 3 ? 0 : 1 }}
      transition={{ duration: 0.5 }}
      data-testid="opening-sequence"
      dir="rtl"
    >
      <div className="text-center px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: step >= 0 ? 1 : 0, scale: step >= 0 ? 1 : 0.94 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-baseline gap-2 mb-8 select-none"
        >
          <span
            className="text-4xl sm:text-5xl font-black tracking-[0.4em]"
            style={{
              color: "#E8D08A",
              textShadow: "0 1px 0 rgba(0,0,0,0.9), 0 0 24px rgba(197,160,89,0.4)",
              fontFamily: "ui-monospace, monospace",
            }}
          >
            OK<span style={{ color: "#7a5a1c" }}>·</span>FBI
          </span>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: step >= 1 ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] sm:text-[12px] font-cairo tracking-[0.35em] text-zinc-400 mb-3"
        >
          جاري الوصول إلى ملفات التحقيق
        </motion.div>
        <div className="mx-auto w-[220px] h-[1px] bg-zinc-800 overflow-hidden">
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: step >= 1 ? "100%" : "0%" }}
            transition={{ duration: 0.9, ease: "easeInOut" }}
            className="h-full"
            style={{ background: "linear-gradient(90deg, transparent, #C5A059, transparent)" }}
          />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: step >= 2 ? 1 : 0 }}
          transition={{ duration: 0.4 }}
          className="mt-4 text-[10px] font-mono-ar tracking-[0.4em] text-emerald-400/80"
        >
          ACCESS · GRANTED
        </motion.div>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════
//  3) HEADER (Public) — transparent → glass on scroll
// ══════════════════════════════════════════════════════════════
function PublicHeader({ authed, isOwner }) {
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const links = [
    ["/", "الرئيسية"],
    ["/about", "عن اللعبة"],
    ["/how-to-play", "طريقة اللعب"],
    ["/case-preview", "معاينة قضية"],
    ["/ranks", "الرتب والشارات"],
    ["/premium-ids", "الأرقام المميزة"],
    ["/leaderboard-public", "المتصدرون"],
    ["/faq", "الأسئلة الشائعة"],
  ];
  return (
    <header
      className={`fixed inset-x-0 top-0 z-[40] transition-all duration-500`}
      style={{
        background: scrolled
          ? "linear-gradient(180deg, rgba(8,8,7,0.92) 0%, rgba(8,8,7,0.70) 100%)"
          : "linear-gradient(180deg, rgba(8,8,7,0.35) 0%, rgba(8,8,7,0.00) 100%)",
        backdropFilter: scrolled ? "blur(14px)" : "blur(0px)",
        WebkitBackdropFilter: scrolled ? "blur(14px)" : "blur(0px)",
        borderBottom: scrolled ? "1px solid rgba(184,138,66,0.22)" : "1px solid transparent",
      }}
      data-testid="public-header"
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
        <Link to="/" className="inline-flex items-baseline gap-2 select-none" data-testid="public-header-logo">
          <span
            className="text-xl font-black tracking-[0.32em]"
            style={{
              color: "#E8D08A",
              textShadow: "0 1px 0 rgba(0,0,0,0.85), 0 0 10px rgba(197,160,89,0.28)",
              fontFamily: "ui-monospace, monospace",
            }}
          >
            OK<span style={{ color: "#7a5a1c" }}>·</span>FBI
          </span>
          <span className="hidden sm:inline text-[9px] tracking-[0.4em] text-zinc-500">INVESTIGATION</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-6 text-[11px] font-cairo tracking-[0.22em] text-zinc-400">
          {links.map(([to, label]) => (
            <Link
              key={to}
              to={to}
              className="hover:text-gold transition-colors"
              data-testid={`public-nav-${to.replace(/^\//, "") || "home"}`}
            >
              {label}
            </Link>
          ))}
        </nav>
        <button
          onClick={() => nav(authed ? "/app" : "/login")}
          data-testid="header-cta"
          className="group inline-flex items-center gap-2 text-[11px] font-cairo tracking-[0.3em] px-4 py-2 relative"
          style={{
            color: "#E8D08A",
            border: "1px solid rgba(184,138,66,0.55)",
            background: "linear-gradient(180deg, rgba(184,138,66,0.10) 0%, rgba(184,138,66,0.04) 100%)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.6) inset",
          }}
        >
          {authed ? "العودة إلى مقر التحقيق" : "دخول كمحقق"}
          <ArrowLeft size={12} className="rtl:rotate-180 group-hover:-translate-x-0.5 transition-transform" />
        </button>
      </div>
    </header>
  );
}
export { PublicHeader };

// ══════════════════════════════════════════════════════════════
export function Hero({ authed, cms, content }) {
  const reduced = useReducedMotion();
  const nav = useNavigate();
  const ref = useRef(null);
  // Backwards-compatible field lookup: prefer OWNER-edited page-content,
  // fall back to legacy cms hero config, then to hard-coded design copy.
  const f = (content && content.fields) || {};
  const eyebrowTxt      = f.eyebrow      || cms?.hero?.eyebrow      || "CLASSIFIED · FILE 001";
  const titleOverride   = f.title_override || cms?.hero?.title_override || "";
  const subtitleTxt     = f.subtitle     || cms?.hero?.subtitle     || "كل قضية تخفي شيئاً. كل دليل قد يغيّر مسار التحقيق. والقرار الأخير… لك.";
  const descriptionTxt  = f.description  || cms?.hero?.description  || "ادخل عالم OK FBI، حلّل الأدلة، ابنِ نظريتك واختبر قدرتك على كشف الحقيقة.";
  const ctaPrimaryTxt   = f.cta_primary_text   || cms?.hero?.cta_primary_text   || "ابدأ التحقيق";
  const ctaPrimaryLink  = f.cta_primary_link   || "/login";
  const ctaSecondaryTxt = f.cta_secondary_text || cms?.hero?.cta_secondary_text || "استكشف المقر";
  const ctaSecondaryLink= f.cta_secondary_link || "/about";
  const bgImageUrl      = f.background_image_url || "";

  // Mouse-reactive parallax layers (small offsets)
  const [mx, setMx] = useState(0);
  const [my, setMy] = useState(0);
  const onMouseMove = (e) => {
    if (reduced) return;
    const b = ref.current?.getBoundingClientRect();
    if (!b) return;
    setMx(((e.clientX - b.left) / b.width - 0.5) * 2);
    setMy(((e.clientY - b.top) / b.height - 0.5) * 2);
  };

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 80]);
  const yFg = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -60]);

  return (
    <section
      id="hero"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen w-full overflow-hidden"
      style={{
        background:
          "radial-gradient(120% 80% at 70% 20%, #17130a 0%, #0a0908 45%, #060605 100%)",
      }}
      data-testid="hero-section"
    >
      {/* Cinematic photographic backdrop — case cover with heavy dark grading */}
      <motion.div
        aria-hidden
        style={{ y: yBg }}
        className="pointer-events-none absolute inset-0"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${bgImageUrl || publicMedia(0)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(35%) contrast(1.05) brightness(0.55) saturate(0.85)",
            opacity: 0.55,
          }}
        />
        {/* dark gradient overlay so Arabic text stays readable */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(4,4,3,0.92) 0%, rgba(4,4,3,0.55) 55%, rgba(4,4,3,0.35) 100%), linear-gradient(180deg, rgba(4,4,3,0.4) 0%, rgba(4,4,3,0.85) 100%)",
          }}
        />
      </motion.div>
      {/* Deep background — massive emblem outline */}
      <motion.div
        aria-hidden
        style={{ y: yBg }}
        className="pointer-events-none absolute inset-0 flex items-center justify-end pr-[6%]"
      >
        <div
          className="w-[75vh] h-[75vh] rounded-full"
          style={{
            border: "1px solid rgba(184,138,66,0.18)",
            boxShadow: "inset 0 0 120px rgba(184,138,66,0.06)",
            background:
              "radial-gradient(closest-side, rgba(184,138,66,0.08) 0%, rgba(184,138,66,0.00) 65%)",
          }}
        />
      </motion.div>

      {/* Faded scan / evidence-map lines (SVG) */}
      <motion.svg
        aria-hidden
        style={{ transform: `translate3d(${mx * -4}px, ${my * -3}px, 0)` }}
        className="pointer-events-none absolute inset-0 w-full h-full opacity-[0.10]"
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <linearGradient id="lg1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#B88A42" stopOpacity="0" />
            <stop offset="50%" stopColor="#B88A42" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#B88A42" stopOpacity="0" />
          </linearGradient>
        </defs>
        {Array.from({ length: 8 }).map((_, i) => (
          <line
            key={i}
            x1={-100}
            y1={100 + i * 90}
            x2={1700}
            y2={200 + i * 70}
            stroke="url(#lg1)"
            strokeWidth="0.5"
          />
        ))}
        {Array.from({ length: 20 }).map((_, i) => (
          <circle key={`c${i}`} cx={(i * 90) % 1600} cy={(i * 47) % 900} r="1.6" fill="#B88A42" opacity="0.6" />
        ))}
      </motion.svg>

      {/* Vignette */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(70% 60% at 50% 50%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%)",
        }}
      />

      {/* Content grid */}
      <div className="relative z-10 max-w-[1280px] mx-auto px-5 sm:px-8 pt-[110px] sm:pt-[140px] pb-[80px] grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        {/* Left — copy */}
        <motion.div
          style={{ y: yFg }}
          className="lg:col-span-7 order-2 lg:order-1"
          dir="rtl"
        >
          <div className="flex items-center gap-3 mb-6" data-testid="hero-eyebrow">
            <span className="h-px w-8 bg-[#B88A42]" />
            <span className="text-[10px] tracking-[0.5em] text-[#B88A42] font-mono-ar">{eyebrowTxt}</span>
          </div>
          <h1
            className="font-cairo font-black text-white leading-[1.15] mb-6"
            style={{ fontSize: "clamp(2rem, 5.2vw, 4rem)" }}
            data-testid="hero-title"
          >
            {titleOverride ? (
              <span style={{ color: "#E8D08A" }}>{titleOverride}</span>
            ) : (
              <>
                هل تملك ما يكفي لكشف{" "}
                <span
                  className="relative inline-block"
                  style={{
                    color: "#E8D08A",
                    background:
                      "linear-gradient(180deg, #E8D08A 0%, #B88A42 55%, #7a5a1c 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    filter: "drop-shadow(0 2px 12px rgba(184,138,66,0.25))",
                  }}
                >
                  الحقيقة
                  <span className="okfbi-sheen" aria-hidden />
                </span>
                ؟
              </>
            )}
          </h1>
          <p
            className="text-zinc-300 font-cairo text-base sm:text-lg leading-[2] mb-4 max-w-[620px]"
            data-testid="hero-lead"
          >
            {subtitleTxt}
          </p>
          <p className="text-zinc-400 font-cairo text-sm sm:text-base leading-[1.9] mb-8 max-w-[600px]">
            {descriptionTxt}
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <PrimaryButton onClick={() => nav(authed ? "/app" : ctaPrimaryLink)} data-testid="hero-primary-cta">
              {authed ? "العودة إلى مقر التحقيق" : ctaPrimaryTxt}
            </PrimaryButton>
            <SecondaryButton onClick={() => nav(ctaSecondaryLink)} data-testid="hero-secondary-cta">
              {ctaSecondaryTxt}
            </SecondaryButton>
          </div>

          <div className="mt-10 flex items-center gap-4 text-[10px] font-mono-ar tracking-[0.35em] text-zinc-500">
            <span>ACCESS // PUBLIC PREVIEW</span>
            <span className="h-px flex-1 max-w-[120px] bg-zinc-800" />
            <span>NO CLEARANCE REQUIRED</span>
          </div>
        </motion.div>

        {/* Right — Interactive Case Dossier */}
        <motion.div
          style={{ transform: `translate3d(${mx * 10}px, ${my * 8}px, 0)` }}
          className="lg:col-span-5 order-1 lg:order-2 flex justify-center"
        >
          <CaseDossier />
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 inset-x-0 flex justify-center z-10 pointer-events-none">
        <motion.div
          animate={reduced ? {} : { y: [0, 8, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
          className="text-[10px] tracking-[0.4em] text-zinc-400 font-cairo flex flex-col items-center gap-1"
        >
          <span>استكشف</span>
          <ChevronDown size={12} />
        </motion.div>
      </div>

      <style>{`
        .okfbi-sheen{
          position: absolute; inset: 0; overflow: hidden; pointer-events: none;
        }
        .okfbi-sheen::before{
          content:''; position:absolute; top:-20%; bottom:-20%; left:0; width:35%;
          background: linear-gradient(90deg, transparent 0%, rgba(232,208,138,0.55) 50%, transparent 100%);
          filter: blur(6px);
          animation: okfbi-sheen 6.5s ease-in-out 2s infinite;
        }
        ${SHEEN_KEYFRAMES}
      `}</style>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════
//  5) BUTTONS — custom OK FBI style
// ══════════════════════════════════════════════════════════════
function PrimaryButton({ children, onClick, ...rest }) {
  return (
    <button
      onClick={onClick}
      {...rest}
      className="group relative inline-flex items-center gap-2 px-6 py-3 font-cairo text-[13px] tracking-[0.3em] transition-transform"
      style={{
        color: "#0a0a0a",
        background:
          "linear-gradient(180deg, #E8D08A 0%, #B88A42 55%, #7a5a1c 100%)",
        border: "1px solid #B88A42",
        boxShadow:
          "0 6px 30px rgba(184,138,66,0.25), inset 0 1px 0 rgba(255,255,255,0.25)",
        borderRadius: 2,
        fontWeight: 800,
      }}
    >
      <span>{children}</span>
      <ArrowLeft size={14} className="rtl:rotate-180 group-hover:-translate-x-0.5 transition-transform" />
    </button>
  );
}
function SecondaryButton({ children, onClick, ...rest }) {
  return (
    <button
      onClick={onClick}
      {...rest}
      className="inline-flex items-center gap-2 px-5 py-3 font-cairo text-[12px] tracking-[0.3em] text-zinc-200 hover:text-gold transition-colors"
      style={{
        border: "1px solid rgba(184,138,66,0.35)",
        background: "rgba(20,17,10,0.35)",
        borderRadius: 2,
      }}
    >
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════
//  6) CASE DOSSIER — the visual centerpiece
// ══════════════════════════════════════════════════════════════
function CaseDossier() {
  const reduced = useReducedMotion();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const rootRef = useRef(null);
  const onMouseMove = (e) => {
    if (reduced) return;
    const b = rootRef.current?.getBoundingClientRect();
    if (!b) return;
    setTilt({
      x: (((e.clientY - b.top) / b.height) - 0.5) * -6, // pitch
      y: (((e.clientX - b.left) / b.width) - 0.5) * 8,  // yaw
    });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px" }}
      transition={{ duration: 1.1, ease: "easeOut" }}
      className="relative w-[min(92vw,440px)]"
      data-testid="case-dossier"
    >
      <div
        ref={rootRef}
        onMouseMove={onMouseMove}
        onMouseLeave={onLeave}
        className="relative"
        style={{
          transform: `perspective(1400px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: "transform 250ms ease-out",
          transformStyle: "preserve-3d",
        }}
      >
        {/* File folder */}
        <div
          className="relative p-5 pb-6"
          style={{
            background:
              "linear-gradient(180deg, rgba(24,20,14,0.94) 0%, rgba(14,12,8,0.94) 100%)",
            border: "1px solid rgba(184,138,66,0.35)",
            boxShadow:
              "0 30px 80px rgba(0,0,0,0.65), inset 0 0 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.4)",
          }}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-[9px] tracking-[0.35em] text-[#B88A42] font-mono-ar">CASE // 001</span>
              <span className="h-px w-8 bg-[#B88A42]/40" />
            </div>
            <ClassifiedStamp label="CONFIDENTIAL" />
          </div>

          {/* Row: photo + fingerprint */}
          <div className="grid grid-cols-5 gap-3 mb-4">
            <div className="col-span-2 aspect-[3/4] relative overflow-hidden" style={{ border: "1px solid rgba(184,138,66,0.35)" }}>
              {/* Public preview media — safe built-in case cover with redaction bar */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${publicMedia(1)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: "grayscale(50%) contrast(1.05) brightness(0.65) saturate(0.7)",
                }}
              />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.8) 100%)" }} />
              <div
                className="absolute inset-x-2 top-[38%] h-[8%]"
                style={{ background: "rgba(0,0,0,0.88)", boxShadow: "0 0 0 1px rgba(184,138,66,0.5)" }}
                aria-hidden
              />
              <div className="absolute bottom-1 left-1 right-1 text-[8px] font-mono-ar tracking-widest text-[#E8D08A]/85">
                SUSPECT · #A-004
              </div>
              <ScanBar />
            </div>
            <div className="col-span-3 flex flex-col gap-2">
              <MetaRow label="الحالة" value="قيد التحقيق" tag="ACTIVE" />
              <MetaRow label="التصنيف" value="سري" tag="CLASSIFIED" />
              <MetaRow label="المحقق المخوّل" value="—" tag="PENDING" />
              <div className="flex-1 relative overflow-hidden" style={{ border: "1px solid rgba(184,138,66,0.3)", background: "rgba(255,255,255,0.02)" }}>
                <Fingerprint size={54} className="absolute -bottom-3 -left-3 text-[#B88A42]/25" strokeWidth={0.9} />
                <div className="absolute inset-0 flex items-end p-2">
                  <div className="text-[9px] font-mono-ar tracking-[0.3em] text-[#B88A42]/70">PRINT · MATCHED</div>
                </div>
                <ScanBar delay={1.6} />
              </div>
            </div>
          </div>

          {/* Redacted document */}
          <div className="mb-4 relative" style={{ border: "1px solid rgba(184,138,66,0.25)", background: "rgba(0,0,0,0.35)" }}>
            <div className="p-3 space-y-1.5">
              {[92, 78, 88, 60, 82].map((w, i) => (
                <div key={i} className="h-[6px] rounded-full bg-zinc-700/60" style={{ width: `${w}%` }} />
              ))}
              <div className="h-[6px] rounded-full" style={{ width: "48%", background: "rgba(0,0,0,0.9)", boxShadow: "0 0 0 1px rgba(184,138,66,0.4)" }} />
            </div>
            <div className="absolute top-1 left-2 text-[8px] font-mono-ar tracking-widest text-[#B88A42]/70">FILE · OPEN</div>
          </div>

          {/* Evidence strip */}
          <div className="flex items-center gap-2">
            {["01", "02", "03", "04", "05"].map((n) => (
              <div key={n} className="flex-1 text-center py-1.5 text-[9px] font-mono-ar tracking-widest text-[#B88A42]/85"
                   style={{ border: "1px solid rgba(184,138,66,0.35)", background: "rgba(184,138,66,0.05)" }}>
                EV·{n}
              </div>
            ))}
          </div>
        </div>

        {/* Paper clip corner */}
        <div className="absolute -top-3 right-6 w-10 h-3 rounded-t-[3px]" style={{ background: "linear-gradient(180deg,#9a9a9a,#3d3d3d)", boxShadow: "0 2px 6px rgba(0,0,0,0.6)" }} aria-hidden />
      </div>
    </motion.div>
  );
}

function MetaRow({ label, value, tag }) {
  return (
    <div className="flex items-center justify-between text-[11px] font-cairo px-2 py-1.5"
         style={{ border: "1px solid rgba(184,138,66,0.22)", background: "rgba(255,255,255,0.02)" }}>
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-200">{value}</span>
      <span className="text-[8px] font-mono-ar tracking-[0.25em] text-[#B88A42]/80">{tag}</span>
    </div>
  );
}

function ClassifiedStamp({ label }) {
  return (
    <div className="relative select-none"
         style={{
           color: "#C15353",
           border: "2px solid rgba(193,83,83,0.75)",
           padding: "3px 8px",
           transform: "rotate(-6deg)",
           fontFamily: "ui-monospace, monospace",
           fontSize: 10,
           letterSpacing: "0.25em",
           fontWeight: 800,
           opacity: 0.88,
           background: "rgba(193,83,83,0.05)",
         }}>
      {label}
    </div>
  );
}

function ScanBar({ delay = 0 }) {
  const reduced = useReducedMotion();
  if (reduced) return null;
  return (
    <div
      aria-hidden
      className="absolute inset-x-0 h-[2px] pointer-events-none"
      style={{
        background: "linear-gradient(90deg, transparent, rgba(184,138,66,0.7), transparent)",
        top: "0%",
        animation: `okfbi-scan 4.5s ease-in-out ${delay}s infinite`,
      }}
    >
      <style>{`
        @keyframes okfbi-scan {
          0%   { top: 0%;   opacity: 0; }
          10%  { opacity: 0.9; }
          50%  { opacity: 0.9; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  7) SECTION scaffolding + Reveal
// ══════════════════════════════════════════════════════════════
function Section({ id, chapter, eyebrow, title, subtitle, children, dark = true }) {
  return (
    <section
      id={id}
      className="relative py-24 sm:py-32"
      style={{
        background: dark
          ? "linear-gradient(180deg, #060605 0%, #0a0908 100%)"
          : "linear-gradient(180deg, #0a0908 0%, #060605 100%)",
      }}
      data-testid={`section-${id}`}
    >
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8" dir="rtl">
        <Reveal>
          <div className="flex items-center gap-4 mb-4">
            {chapter && (
              <span className="text-[10px] tracking-[0.55em] text-[#B88A42] font-mono-ar">CH · {chapter}</span>
            )}
            <span className="h-px flex-1 max-w-[120px] bg-[#B88A42]/25" />
            {eyebrow && <span className="text-[10px] tracking-[0.5em] text-zinc-500 font-mono-ar">{eyebrow}</span>}
          </div>
          <h2
            className="font-cairo font-black text-white leading-[1.2] mb-3"
            style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.4rem)" }}
          >
            {title}
          </h2>
          {subtitle && (
            <p className="text-zinc-400 font-cairo text-base leading-[1.9] max-w-[720px] mb-10">{subtitle}</p>
          )}
        </Reveal>
        {children}
      </div>
    </section>
  );
}
function Reveal({ children, delay = 0, className = "" }) {
  const reduced = useReducedMotion();

  // Fail-visible: with no IntersectionObserver, whileInView never fires and the
  // section would keep its box at opacity:0 — the "huge empty gap" bug. Render
  // it plainly instead. Same for reduced-motion.
  if (reduced || (typeof window !== "undefined" && !("IntersectionObserver" in window))) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px" }}
      transition={{ duration: 0.9, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════
//  8) HOW IT WORKS — Investigation Flow
// ══════════════════════════════════════════════════════════════
const FLOW = [
  { n: "01", t: "استلم القضية", d: "افتح الملف السري وابدأ من نقطة الصفر." },
  { n: "02", t: "افحص الأدلة", d: "بصمات، كاميرات، وثائق، محادثات وتقارير." },
  { n: "03", t: "اربط الخيوط", d: "بين المشتبه بهم، الأماكن والأوقات." },
  { n: "04", t: "كوّن نظريتك", d: "اجمع الحقائق واختر رواية التحقيق." },
  { n: "05", t: "صوّت", d: "شارك بقرارك مع بقية المحققين." },
  { n: "06", t: "اكشف الحقيقة", d: "الحل النهائي يُعلَن من المالك." },
  { n: "07", t: "اكسب XP وارتقِ", d: "المحققون الصائبون يصعدون في السلّم." },
];
export function HowItWorks({ content }) {
  const f = (content && content.fields) || {};
  const items = (content && Array.isArray(content.items) && content.items.length)
    ? content.items.filter((i) => i.visible !== false).map((it) => ({ n: it.n || "", t: it.title || "", d: it.description || "" }))
    : FLOW;
  return (
    <Section
      id="how"
      chapter={f.chapter || "02"}
      eyebrow={
        // Force Arabic eyebrow — legacy CMS value "INVESTIGATION FLOW" is
        // translated to the required Arabic string per P3 acceptance.
        (!f.eyebrow || /^INVESTIGATION\s+FLOW$/i.test(String(f.eyebrow).trim()))
          ? "مراحل التحقيق"
          : f.eyebrow
      }
      title={f.title || "كيف يبدأ التحقيق؟"}
      subtitle={f.subtitle || "سبع مراحل تقودك من فتح الملف حتى كشف الحقيقة. كل خطوة تبني الخطوة التي تليها."}
    >
      <div className="relative" dir="rtl">
        {/* Vertical timeline spine — mobile: pinned to the right; desktop: centered */}
        <div aria-hidden
             className="absolute top-0 bottom-0 w-px right-[23px] lg:right-1/2 lg:translate-x-[0.5px] bg-gradient-to-b from-[#B88A42]/60 via-[#B88A42]/20 to-transparent" />
        <div className="space-y-6 sm:space-y-8">
          {items.map((s, i) => {
            const isRightSide = i % 2 === 0; // desktop: even = card on the right of the spine, odd = card on the left
            return (
              <Reveal key={s.n || i} delay={i * 0.05}>
                {/* Mobile (< lg): 2-track [46-circle | card].  Desktop (lg+): 3-track
                    [1fr | 46 | 1fr] with EXPLICIT col-start so the circle is
                    ALWAYS on column 2, and the card takes the correct 1fr side
                    with min-w-0 to prevent Arabic breaking char-by-char. */}
                <div className="grid gap-4 lg:gap-8 items-start
                                grid-cols-[46px_minmax(0,1fr)]
                                lg:grid-cols-[minmax(0,1fr)_46px_minmax(0,1fr)]">
                  <div className="lg:col-start-2 lg:row-start-1 relative flex justify-center pt-1">
                    <div
                      className="w-[46px] h-[46px] rounded-full flex items-center justify-center text-[11px] font-mono-ar tracking-widest shrink-0"
                      style={{
                        color: "#E8D08A",
                        border: "1px solid rgba(184,138,66,0.55)",
                        background: "radial-gradient(circle, rgba(184,138,66,0.12) 0%, rgba(0,0,0,0) 70%)",
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.5)",
                      }}
                    >
                      {s.n}
                    </div>
                  </div>
                  <div
                    className={`min-w-0 p-4 sm:p-5 lg:row-start-1 ${
                      isRightSide ? "lg:col-start-3" : "lg:col-start-1"
                    }`}
                    style={{
                      border: "1px solid rgba(184,138,66,0.18)",
                      background: "rgba(20,17,10,0.35)",
                      wordBreak: "normal",
                      overflowWrap: "break-word",
                    }}
                  >
                    <div className="text-white font-cairo text-lg font-black mb-1">{s.t}</div>
                    <div className="text-zinc-400 font-cairo text-sm leading-relaxed">{s.d}</div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════
//  9) CASE PREVIEW — Fictional demo dossier
// ══════════════════════════════════════════════════════════════
const DEMO_EVIDENCE = [
  { n: "01", label: "بصمة جزئية",              tag: "FINGERPRINT",  cover: 0, Icon: Fingerprint },
  { n: "02", label: "لقطة كاميرا مراقبة",      tag: "CCTV FRAME",   cover: 1, Icon: Camera },
  { n: "03", label: "مذكرة مكتوبة بخط اليد",  tag: "HANDWRITTEN", cover: 2, Icon: FileText },
  { n: "04", label: "شظية من خط زمني",         tag: "TIMELINE",     cover: 3, Icon: Clock },
  { n: "05", label: "جزء من خريطة الموقع",     tag: "LOCATION",     cover: 4, Icon: MapPin },
];

export function CasePreview({ content }) {
  const nav = useNavigate();
  const [sel, setSel] = useState(null);
  const f = (content && content.fields) || {};
  const iconMap = { Fingerprint, Camera, FileText, Clock, MapPin, ScanLine };
  const evidence = (content && Array.isArray(content.items) && content.items.length)
    ? content.items.filter((i) => i.visible !== false).map((it) => ({
        n: it.n || "",
        label: it.label || "",
        tag: it.tag || "",
        cover_url: it.cover_url || "",
        cover: Number.isFinite(it.cover_index) ? it.cover_index : 0,
        Icon: iconMap[it.icon] || ScanLine,
      }))
    : DEMO_EVIDENCE;
  const caseCover = f.case_cover_url && f.case_cover_url.trim() !== ""
    ? f.case_cover_url
    : publicMedia(Number.isFinite(f.case_cover_index) ? f.case_cover_index : 3);
  return (
    <Section
      id="case"
      chapter={f.chapter || "03"}
      eyebrow={f.eyebrow || "OPEN FILE · PUBLIC DEMO"}
      title={f.title || "ملف مفتوح: لغز الفيلا المهجورة"}
      subtitle={f.subtitle || "معاينة عامة لقضية خيالية بالكامل. مرّر الأدلة، ادرسها، وحاول أن تربط الخيوط… لكن الحفظ والتصويت يتطلبان حساب محقق."}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Case briefing */}
        <div className="lg:col-span-1 p-0 overflow-hidden relative" style={{ border: "1px solid rgba(184,138,66,0.28)", background: "rgba(14,12,8,0.75)" }}>
          {/* Main fictional case image — public preview asset */}
          <div className="relative aspect-[16/9] overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${caseCover})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                filter: "grayscale(25%) contrast(1.05) brightness(0.75) saturate(0.85)",
              }}
              aria-hidden
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(14,12,8,0.95) 100%)" }}
              aria-hidden
            />
            <div className="absolute bottom-2 right-3 flex items-center gap-2">
              <span className="text-[9px] tracking-[0.35em] text-[#E8D08A] font-mono-ar">{f.cover_label || "EXTERIOR · NIGHT"}</span>
              <span className="h-px w-6 bg-[#B88A42]/60" />
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] tracking-[0.4em] text-[#B88A42] font-mono-ar">{f.case_code || "CASE // 001"}</span>
              <span className="text-[9px] tracking-[0.3em] text-emerald-400/70 font-mono-ar">{f.case_status_text || "STATUS · OPEN"}</span>
            </div>
            <div className="font-cairo text-white text-xl font-black mb-2">{f.case_title || "لغز الفيلا المهجورة"}</div>
            <div className="text-zinc-400 text-sm leading-[1.9] font-cairo mb-4">
              {f.case_description || "بعد أشهر من الهدوء، عادت الأضواء لتُشعَل في الفيلا المهجورة على أطراف المدينة. في الصباح، وُجدت غرفة المكتب مقلوبة ولا يوجد أثر لاقتحام. من كان هناك؟ ولماذا؟"}
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              <MetaChip label={f.meta_classification_label || "التصنيف"}   value={f.meta_classification_value || "سري"} />
              <MetaChip label={f.meta_type_label           || "النوع"}      value={f.meta_type_value          || "لغز"} />
              <MetaChip label={f.meta_evidence_label       || "الأدلة"}     value={f.meta_evidence_value      || "05"}  />
              <MetaChip label={f.meta_suspects_label       || "مشتبه بهم"}  value={f.meta_suspects_value      || "04"}  />
            </div>
            <SecondaryButton onClick={() => nav(f.cta_login_link || "/login")} data-testid="demo-login-cta">{f.cta_login_text || "سجّل الدخول للمتابعة"}</SecondaryButton>
          </div>
        </div>

        {/* Evidence board */}
        <div className="lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.4em] text-zinc-500 font-mono-ar">{f.board_heading || "EVIDENCE BOARD"}</span>
            <span className="text-[9px] tracking-[0.3em] text-[#B88A42]/70 font-mono-ar">{f.board_hint || "HOVER TO INSPECT"}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {evidence.map((e) => (
              <EvidenceCard
                key={e.n || Math.random()}
                data={e}
                onSelect={() => setSel(e)}
                data-testid={`evidence-${e.n}`}
              />
            ))}
          </div>
          {sel && (
            <EvidenceInspector data={sel} onClose={() => setSel(null)} onGoLogin={() => nav(f.cta_login_link || "/login")} />
          )}
        </div>
      </div>
    </Section>
  );
}
function MetaChip({ label, value }) {
  return (
    <div className="px-3 py-2 text-[11px] font-cairo flex items-center justify-between"
         style={{ border: "1px solid rgba(184,138,66,0.22)", background: "rgba(255,255,255,0.02)" }}>
      <span className="text-zinc-500">{label}</span>
      <span className="text-zinc-200">{value}</span>
    </div>
  );
}
function EvidenceCard({ data, onSelect, ...rest }) {
  const reduced = useReducedMotion();
  const Icon = data.Icon || ScanLine;
  return (
    <motion.button
      whileHover={reduced ? {} : { y: -3, scale: 1.01 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      onClick={onSelect}
      {...rest}
      className="group text-right p-3 relative overflow-hidden"
      style={{
        border: "1px solid rgba(184,138,66,0.25)",
        background: "linear-gradient(180deg, rgba(24,20,14,0.75) 0%, rgba(10,9,8,0.85) 100%)",
        borderRadius: 2,
      }}
      dir="rtl"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9px] tracking-[0.35em] text-[#B88A42]/85 font-mono-ar">EVIDENCE // {data.n}</span>
        <span className="text-[8px] tracking-[0.3em] text-zinc-500 font-mono-ar">{data.tag}</span>
      </div>
      <div className="aspect-[4/3] mb-2 relative overflow-hidden"
        style={{ border: "1px solid rgba(184,138,66,0.22)", background: "#0b0a08" }}>
        {/* Public preview image */}
        <div
          className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.04]"
          style={{
            backgroundImage: `url(${data.cover_url && data.cover_url.trim() !== "" ? data.cover_url : publicMedia(data.cover ?? 0)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(45%) contrast(1.05) brightness(0.55) saturate(0.85)",
          }}
          aria-hidden
        />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.75) 100%)",
        }} aria-hidden />
        <div className="absolute inset-0 flex items-center justify-center opacity-90">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              border: "1px solid rgba(232,208,138,0.55)",
              background: "radial-gradient(circle, rgba(184,138,66,0.25) 0%, rgba(0,0,0,0) 70%)",
              backdropFilter: "blur(4px)",
            }}
          >
            <Icon size={20} className="text-[#E8D08A]" strokeWidth={1.4} />
          </div>
        </div>
        {/* corner label */}
        <div className="absolute top-1.5 left-1.5 text-[8px] font-mono-ar tracking-widest text-[#E8D08A]/80">
          EV·{data.n}
        </div>
      </div>
      <div className="text-white font-cairo text-[13px] font-black">{data.label}</div>
      <div className="text-zinc-500 font-cairo text-[10px] mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
        EVIDENCE · SELECTED
      </div>
    </motion.button>
  );
}
function EvidenceInspector({ data, onClose, onGoLogin }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mt-4 p-5"
      style={{ border: "1px solid rgba(184,138,66,0.32)", background: "linear-gradient(180deg, rgba(28,23,15,0.9) 0%, rgba(10,9,8,0.9) 100%)" }}
      data-testid="evidence-inspector"
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-[0.4em] text-[#B88A42] font-mono-ar">INSPECTING · EVIDENCE // {data.n}</span>
        <button onClick={onClose} className="text-zinc-500 hover:text-gold text-[11px] font-cairo tracking-widest">إغلاق</button>
      </div>
      <div className="text-white font-cairo text-lg font-black mb-2">{data.label}</div>
      <p className="text-zinc-400 font-cairo text-sm leading-[1.9] mb-4">
        هذا الدليل يفتح مساراً محتملاً في التحقيق. لحفظ ملاحظاتك أو تسجيل نظريتك رسمياً، تحتاج إلى حساب محقق موثّق.
      </p>
      <div className="flex flex-wrap gap-3">
        <PrimaryButton onClick={onGoLogin}>سجّل دخولك كمحقق</PrimaryButton>
        <SecondaryButton onClick={onClose}>متابعة المعاينة</SecondaryButton>
      </div>
    </motion.div>
  );
}

// ══════════════════════════════════════════════════════════════
//  10) CONNECT THE DOTS — visual line connections
// ══════════════════════════════════════════════════════════════
export function ConnectDots({ content }) {
  const f = (content && content.fields) || {};
  const coverIdx = Number.isFinite(f.background_cover_index) ? f.background_cover_index : 2;
  return (
    <Section
      id="connect"
      chapter={f.chapter || "04"}
      eyebrow={f.eyebrow || "THREADS"}
      title={f.title || "كل تفصيل قد يكون خيطاً"}
      subtitle={f.subtitle || "تعلَّم كيف يربط المحقق بين المشتبه به، الدليل، المكان، الوقت والدافع."}
    >
      <div className="relative aspect-[16/8] w-full overflow-hidden"
           style={{ border: "1px solid rgba(184,138,66,0.22)" }}>
        {/* Investigation board photographic backdrop */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${publicMedia(coverIdx)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(60%) contrast(1.05) brightness(0.45) saturate(0.7)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(65% 80% at 50% 55%, rgba(6,6,5,0.4) 0%, rgba(6,6,5,0.92) 100%)" }}
          aria-hidden
        />
        <svg viewBox="0 0 800 400" className="absolute inset-0 w-full h-full">
          <defs>
            <linearGradient id="lineg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#B88A42" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#B88A42" stopOpacity="0.15" />
            </linearGradient>
          </defs>
          <Node cx={140} cy={80}  label="مشتبه A" tag="SUSPECT" />
          <Node cx={640} cy={80}  label="دافع" tag="MOTIVE" />
          <Node cx={400} cy={200} label="دليل 02" tag="EV·02" primary />
          <Node cx={140} cy={320} label="الوقت 02:14" tag="TIME" />
          <Node cx={640} cy={320} label="الفيلا · الطابق 1" tag="PLACE" />
          <AnimLine d="M 158 92 Q 300 140 384 190" delay={0.15} />
          <AnimLine d="M 620 92 Q 500 140 416 190" delay={0.4} />
          <AnimLine d="M 158 308 Q 300 260 384 210" delay={0.65} />
          <AnimLine d="M 620 308 Q 500 260 416 210" delay={0.9} />
        </svg>
      </div>
    </Section>
  );
}
function Node({ cx, cy, label, tag, primary }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={primary ? 22 : 16} fill="rgba(0,0,0,0.7)" stroke="rgba(184,138,66,0.7)" strokeWidth={primary ? 1.4 : 1} />
      <text x={cx} y={cy - 30} textAnchor="middle" fontSize="10" fontFamily="ui-monospace, monospace" fill="#B88A42" letterSpacing="2">{tag}</text>
      <text x={cx} y={cy + 40} textAnchor="middle" fontSize="12" fontFamily="Cairo, sans-serif" fill="#e5e5e5">{label}</text>
    </g>
  );
}
function AnimLine({ d, delay = 0 }) {
  const reduced = useReducedMotion();
  return (
    <motion.path
      d={d}
      fill="none"
      stroke="url(#lineg)"
      strokeWidth={1.1}
      initial={{ pathLength: 0, opacity: 0 }}
      whileInView={{ pathLength: 1, opacity: reduced ? 1 : 0.9 }}
      viewport={{ once: true }}
      transition={{ duration: 1.4, delay, ease: "easeInOut" }}
    />
  );
}

// ══════════════════════════════════════════════════════════════
//  11) THEORY PREVIEW
// ══════════════════════════════════════════════════════════════
export function TheoryPreview({ content }) {
  const nav = useNavigate();
  const f = (content && content.fields) || {};
  const slots = (content && Array.isArray(content.items) && content.items.length)
    ? content.items.filter((i) => i.visible !== false)
    : [
        { label: "المشتبه به",    placeholder: "؟" },
        { label: "الدافع",          placeholder: "؟" },
        { label: "الدليل الحاسم", placeholder: "؟" },
      ];
  const coverIdx = Number.isFinite(f.background_cover_index) ? f.background_cover_index : 4;
  return (
    <Section
      id="theory"
      chapter={f.chapter || "05"}
      eyebrow={f.eyebrow || "BUILD YOUR THEORY"}
      title={f.title || "كوّن نظريتك"}
      subtitle={f.subtitle || "ليس كل دليل كما يبدو. اجمع القطع، وقرّر مَن الفاعل، لماذا، وكيف."}
    >
      <div className="relative overflow-hidden p-6 sm:p-8" style={{ border: "1px solid rgba(184,138,66,0.28)" }}>
        {/* Workspace photographic backdrop */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${publicMedia(coverIdx)})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(55%) contrast(1.05) brightness(0.4) saturate(0.75)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(6,6,5,0.6) 0%, rgba(6,6,5,0.92) 100%)" }}
          aria-hidden
        />
        <div className="relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {slots.map((s, i) => (
              <TheorySlot key={s.id || i} label={s.label} placeholder={s.placeholder} />
            ))}
          </div>
          <div className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
               style={{ border: "1px solid rgba(184,138,66,0.28)", background: "rgba(10,9,8,0.85)" }}>
            <div className="text-white font-cairo text-lg">{f.cta_prompt || "هل أنت مستعد لاتخاذ قرارك؟"}</div>
            <PrimaryButton onClick={() => nav(f.cta_link || "/login")} data-testid="theory-cta">{f.cta_text || "سجّل دخولك لحفظ نظريتك"}</PrimaryButton>
          </div>
        </div>
      </div>
    </Section>
  );
}
function TheorySlot({ label, placeholder }) {
  return (
    <div className="p-5" style={{ border: "1px dashed rgba(184,138,66,0.35)", background: "rgba(20,17,10,0.55)" }}>
      <div className="text-[10px] tracking-[0.35em] text-[#B88A42] font-mono-ar mb-2">{label}</div>
      <div className="text-4xl text-zinc-500 font-mono-ar text-center py-6">{placeholder}</div>
    </div>
  );
}

function RankInsignia({ tier = 0 }) {
  // Chevron/star insignia growing with tier — subtle metallic silhouette.
  const chev = Math.min(tier, 3);
  return (
    <svg viewBox="0 0 64 64" width="56" height="56" aria-hidden>
      <defs>
        <linearGradient id={`ri-${tier}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E8D08A" />
          <stop offset="60%" stopColor="#B88A42" />
          <stop offset="100%" stopColor="#3a2c14" />
        </linearGradient>
      </defs>
      <circle cx="32" cy="32" r="26" fill="none" stroke={`url(#ri-${tier})`} strokeWidth="1" opacity="0.55" />
      {tier >= 4 && (
        <path d="M32 12 L36 22 L46 22 L38 28 L41 38 L32 32 L23 38 L26 28 L18 22 L28 22 Z"
              fill={`url(#ri-${tier})`} opacity="0.92" />
      )}
      {Array.from({ length: chev + 1 }).map((_, i) => (
        <path key={i}
              d={`M18 ${44 - i * 6} L32 ${38 - i * 6} L46 ${44 - i * 6}`}
              fill="none" stroke={`url(#ri-${tier})`} strokeWidth="2" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function BadgeArt({ tier = 0 }) {
  // Tier drives subtle colour variation. Same silhouette; different metalwork.
  const rims = ["#8a6a2b", "#B88A42", "#D6A653", "#E8D08A", "#F5E29B", "#B88A42"];
  const cores = ["#3a2c14", "#4a3617", "#5c421b", "#7a5a1c", "#B88A42", "#4a3617"];
  return (
    <svg viewBox="0 0 80 80" width="60" height="60" aria-hidden>
      <defs>
        <radialGradient id={`bg-core-${tier}`} cx="50%" cy="35%" r="65%">
          <stop offset="0%" stopColor={rims[tier % rims.length]} stopOpacity="0.95" />
          <stop offset="55%" stopColor={cores[tier % cores.length]} stopOpacity="0.95" />
          <stop offset="100%" stopColor="#0a0908" stopOpacity="1" />
        </radialGradient>
        <linearGradient id={`bg-rim-${tier}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E8D08A" />
          <stop offset="55%" stopColor={rims[tier % rims.length]} />
          <stop offset="100%" stopColor="#3a2c14" />
        </linearGradient>
      </defs>
      {/* Shield outline */}
      <path d="M40 4 L72 14 V38 C72 55 58 68 40 74 C22 68 8 55 8 38 V14 Z"
            fill={`url(#bg-core-${tier})`} stroke={`url(#bg-rim-${tier})`} strokeWidth="1.6" />
      <path d="M40 10 L66 18 V38 C66 52 55 62 40 68 C25 62 14 52 14 38 V18 Z"
            fill="none" stroke="rgba(232,208,138,0.35)" strokeWidth="0.6" />
      {/* Star */}
      <path d="M40 26 L44 36 L55 37 L46 44 L49 55 L40 49 L31 55 L34 44 L25 37 L36 36 Z"
            fill="#E8D08A" opacity="0.92" />
      {/* Bottom ribbon marker */}
      <rect x="34" y="60" width="12" height="5" fill="#3a2c14" stroke="rgba(232,208,138,0.5)" strokeWidth="0.4" />
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════
//  12) RANKS & BADGES
// ══════════════════════════════════════════════════════════════
export function RanksAndBadges({ ranks, header, badges }) {
  const hf = (header && header.fields) || {};
  const BADGES = (badges && Array.isArray(badges.items) && badges.items.length)
    ? badges.items.filter((b) => b.visible !== false).map((b) => ({ id: b.id, name: b.name, cond: b.cond, accent_color: b.accent_color || "" }))
    : [
        { name: "أول قضية", cond: "افتح ملف قضيتك الأولى" },
        { name: "نظرية دقيقة", cond: "أرسل نظرية تتوافق مع الحل" },
        { name: "دليل حاسم", cond: "اكشف دليلاً مفصلياً" },
        { name: "لا يخطئ", cond: "3 قضايا صحيحة متتالية" },
        { name: "الليل والصبر", cond: "شارك في تحقيق مباشر" },
        { name: "عين المحقق", cond: "افحص 50 دليلاً" },
      ];
  return (
    <Section
      id="ranks"
      chapter={hf.chapter || "06"}
      eyebrow={hf.eyebrow || "AGENCY WALL"}
      title={hf.title || "لكل محقق سجل"}
      subtitle={hf.subtitle || "الرتب تُبنى بالخبرة الحقيقية داخل التحقيقات، والشارات تُمنَح على المحطات المفصلية."}
    >
      <div className="mb-10">
        <div className="text-[10px] tracking-[0.4em] text-zinc-500 font-mono-ar mb-4">{hf.rank_heading || "RANK · PROGRESSION"}</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {(ranks || []).map((r, i) => (
            <Reveal key={r.id} delay={i * 0.06}>
              <div
                className="p-4 relative overflow-hidden text-center"
                style={{
                  border: "1px solid rgba(184,138,66,0.28)",
                  background: "linear-gradient(180deg, rgba(20,17,10,0.65) 0%, rgba(10,9,8,0.85) 100%)",
                }}
              >
                <div className="mx-auto mb-2" style={{ filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.7))" }}>
                  <RankInsignia tier={i} />
                </div>
                <div className="text-[9px] tracking-[0.4em] text-[#B88A42] font-mono-ar mb-2">RANK · {String(i + 1).padStart(2, "0")}</div>
                <div className="text-white font-cairo text-lg font-black mb-1">{r.name}</div>
                <div className="text-zinc-400 text-[11px] font-cairo">من {r.min_points} XP</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="text-[10px] tracking-[0.4em] text-zinc-500 font-mono-ar mb-4">{hf.badge_heading || "BADGE · WALL"}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {BADGES.map((b, i) => (
          <Reveal key={b.id || b.name} delay={i * 0.05}>
            <div
              className="group p-4 relative overflow-hidden text-center transition-transform hover:-translate-y-1"
              style={{
                border: `1px solid ${b.accent_color ? (b.accent_color + "cc") : "rgba(184,138,66,0.28)"}`,
                background: `radial-gradient(circle at 50% 30%, ${b.accent_color ? (b.accent_color + "22") : "rgba(184,138,66,0.10)"} 0%, rgba(0,0,0,0) 60%)`,
              }}
            >
              <div
                className="mx-auto w-16 h-16 flex items-center justify-center mb-3 relative"
                style={{
                  filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.7))",
                }}
              >
                <BadgeArt tier={i} />
              </div>
              <div className="text-white font-cairo text-[13px] font-black">{b.name}</div>
              <div className="text-zinc-500 text-[11px] font-cairo mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {b.cond}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════
//  13) AGENT ID + PREMIUM IDs + MEMBERSHIPS (no prices!)
// ══════════════════════════════════════════════════════════════
export function AgentIdShowcase({ header, premium, memberships }) {
  const reduced = useReducedMotion();
  const cardRef = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    if (reduced) return;
    const b = cardRef.current?.getBoundingClientRect();
    if (!b) return;
    setTilt({
      x: (((e.clientY - b.top) / b.height) - 0.5) * -8,
      y: (((e.clientX - b.left) / b.width) - 0.5) * 10,
    });
  };
  const hf = (header && header.fields) || {};
  const pf = (premium && premium.fields) || {};
  const mf = (memberships && memberships.fields) || {};
  const PREMIUM = (premium && Array.isArray(premium.items) && premium.items.length)
    ? premium.items.filter((i) => i.visible !== false)
    : [
        { id: "d_royal",     tier: "ملكي",     note: "الأرقام الملكية النادرة", cta: "قريباً", frame: "royal",   sample: "#1",   accent: "#F5E29B" },
        { id: "d_legendary", tier: "أسطوري", note: "أرقام أحادية أو خاصة",     cta: "قريباً", frame: "notched", sample: "#11",  accent: "#E8D08A" },
        { id: "d_vip",       tier: "VIP",       note: "أرقام متكررة لافتة",     cta: "قريباً", frame: "beveled", sample: "#111", accent: "#D6A653" },
      ];
  const MEMBERSHIPS = (memberships && Array.isArray(memberships.items) && memberships.items.length)
    ? memberships.items.filter((i) => i.visible !== false)
    : [
        { id: "d_std",    key: "STANDARD ACCESS", ar: "الوصول الأساسي", note: "دخول العالم وتصفح القضايا العامة", status: "متاح",  badge: "FREE",    features: [] },
        { id: "d_prem",   key: "PREMIUM ACCESS",  ar: "الوصول المميز", note: "خصائص إضافية للتحقيق — قريباً",     status: "قريباً", badge: "VIP",     features: [] },
        { id: "d_stream", key: "STREAM ACCESS",   ar: "وصول الستريمر", note: "أدوات البث والتفاعل المباشر — قريباً", status: "قريباً", badge: "STREAMER", features: [] },
      ];
  return (
    <Section
      id="agent-id"
      chapter={hf.chapter || "07"}
      eyebrow={hf.eyebrow || "IDENTITY"}
      title={hf.title || "بطاقة المحقق"}
      subtitle={hf.subtitle || "رقم المحقق (Agent ID) هوية دائمة داخل OK FBI. مستقل تماماً عن نوع العضوية."}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-14">
        <div className="lg:col-span-5 flex justify-center">
          <div
            ref={cardRef}
            onMouseMove={onMove}
            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
            className="relative w-[min(92vw,360px)] aspect-[16/10] overflow-hidden"
            style={{
              transform: `perspective(1400px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
              transition: "transform 260ms ease-out",
              background:
                "linear-gradient(135deg, #1a1611 0%, #0a0908 55%, #050403 100%)",
              border: "1px solid rgba(184,138,66,0.5)",
              boxShadow: "0 30px 80px rgba(0,0,0,0.65), inset 0 0 40px rgba(184,138,66,0.08)",
              borderRadius: 4,
            }}
            data-testid="agent-id-card"
          >
            <div className="absolute inset-0 p-5 flex flex-col justify-between" dir="rtl">
              {/* Fingerprint watermark */}
              <Fingerprint
                size={220}
                className="absolute -bottom-8 -left-6 text-[#B88A42]/12"
                strokeWidth={0.5}
                aria-hidden
              />
              {/* Microtext strip */}
              <div
                aria-hidden
                className="absolute inset-x-4 bottom-[3px] text-[6px] font-mono-ar tracking-[0.4em] text-[#B88A42]/40 whitespace-nowrap overflow-hidden pointer-events-none"
              >
                OK·FBI · INVESTIGATION DIVISION · CLASSIFIED · CREDENTIAL · OK·FBI · INVESTIGATION DIVISION · CLASSIFIED · CREDENTIAL
              </div>
              {/* Embossed emblem */}
              <div
                aria-hidden
                className="absolute right-3 top-3 w-10 h-10 rounded-full flex items-center justify-center"
                style={{
                  border: "1px solid rgba(232,208,138,0.4)",
                  background: "radial-gradient(circle, rgba(184,138,66,0.20) 0%, rgba(0,0,0,0) 70%)",
                  boxShadow: "inset 0 0 8px rgba(232,208,138,0.15)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M12 1.6 L21.5 6.2 V13 C21.5 18 17.2 21.4 12 22.4 C6.8 21.4 2.5 18 2.5 13 V6.2 Z"
                    fill="none" stroke="#E8D08A" strokeWidth="1.2" opacity="0.75" />
                </svg>
              </div>

              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[16px] font-black tracking-[0.32em]" style={{ color: "#E8D08A", fontFamily: "ui-monospace, monospace" }}>
                    OK<span style={{ color: "#7a5a1c" }}>·</span>FBI
                  </span>
                  <span className="text-[9px] tracking-[0.4em] text-zinc-500">INVESTIGATION</span>
                </div>
                <span className="text-[9px] tracking-[0.35em] text-emerald-400/80 font-mono-ar">{hf.card_status_text || "STATUS · ACTIVE"}</span>
              </div>
              <div className="flex items-end justify-between relative z-10">
                <div>
                  <div className="text-[9px] tracking-[0.4em] text-[#B88A42]/80 font-mono-ar mb-1">AGENT ID</div>
                  <div
                    className="text-4xl sm:text-5xl font-black tracking-[0.15em]"
                    style={{ color: "#E8D08A", textShadow: "0 2px 0 rgba(0,0,0,0.9), 0 0 18px rgba(184,138,66,0.25)", fontFamily: "ui-monospace, monospace" }}
                  >
                    {hf.card_agent_id || "#111"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[9px] tracking-[0.4em] text-[#B88A42]/80 font-mono-ar mb-1">الرتبة</div>
                  <div className="text-white font-cairo text-lg font-black">{hf.card_rank_label || "محقق"}</div>
                </div>
              </div>
            </div>
            {/* light reflection */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(${105 + tilt.y * 6}deg, transparent 40%, rgba(232,208,138,0.16) 50%, transparent 60%)`,
              }}
            />
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="text-[10px] tracking-[0.4em] text-zinc-500 font-mono-ar mb-3">{pf.heading || "PREMIUM AGENT IDs"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PREMIUM.map((p) => (
              <div
                key={p.id || p.tier}
                className="p-4 relative overflow-hidden group"
                style={{
                  border: `1px solid ${p.accent}55`,
                  background: `linear-gradient(180deg, ${p.accent}12 0%, rgba(10,9,8,0.9) 100%)`,
                  boxShadow: p.frame === "royal"
                    ? `inset 0 0 24px ${p.accent}22, 0 8px 22px rgba(0,0,0,0.55)`
                    : "inset 0 0 12px rgba(0,0,0,0.5)",
                }}
                data-testid={`premium-id-${p.tier}`}
              >
                {/* Frame variant treatments */}
                {p.frame === "double" && (
                  <div aria-hidden className="absolute inset-1.5 pointer-events-none"
                       style={{ border: `1px dashed ${p.accent}55` }} />
                )}
                {p.frame === "beveled" && (
                  <>
                    <div aria-hidden className="absolute top-0 left-0 w-5 h-5 border-t border-l pointer-events-none" style={{ borderColor: p.accent }} />
                    <div aria-hidden className="absolute bottom-0 right-0 w-5 h-5 border-b border-r pointer-events-none" style={{ borderColor: p.accent }} />
                  </>
                )}
                {p.frame === "notched" && (
                  <div aria-hidden className="absolute inset-x-3 top-2 h-px pointer-events-none"
                       style={{ background: `linear-gradient(90deg, transparent, ${p.accent}, transparent)` }} />
                )}
                {p.frame === "royal" && (
                  <>
                    <div aria-hidden className="absolute inset-2 pointer-events-none"
                         style={{ border: `1px solid ${p.accent}55` }} />
                    <div aria-hidden className="absolute inset-x-6 top-3 flex items-center gap-1 pointer-events-none">
                      <span className="h-px flex-1" style={{ background: p.accent }} />
                      <svg width="10" height="10" viewBox="0 0 24 24"><path d="M12 2 L15 9 L22 9 L16 14 L18 22 L12 17 L6 22 L8 14 L2 9 L9 9 Z" fill={p.accent} /></svg>
                      <span className="h-px flex-1" style={{ background: p.accent }} />
                    </div>
                  </>
                )}

                <div className="relative flex items-center justify-between mb-2">
                  <div className="text-white font-cairo text-lg font-black">{p.tier}</div>
                  <span className="text-[9px] tracking-[0.35em] text-[#B88A42]/80 font-mono-ar">{p.cta}</span>
                </div>
                {/* Sample number chip */}
                <div className="relative flex items-baseline justify-between">
                  <div className="text-zinc-400 font-cairo text-[12px]">{p.note}</div>
                  <div
                    className="font-black tabular-nums tracking-[0.15em]"
                    style={{
                      color: p.accent,
                      textShadow: "0 1px 0 rgba(0,0,0,0.85), 0 0 12px rgba(184,138,66,0.25)",
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 22,
                    }}
                  >
                    {p.sample}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-cairo text-zinc-500 leading-relaxed">
            {pf.footnote || "الأرقام المميزة نظام مستقل عن نوع العضوية. تدل على الهوية والندرة، ولا تعرض المنصة أي أسعار حالياً."}
          </div>
        </div>
      </div>

      <div className="text-[10px] tracking-[0.4em] text-zinc-500 font-mono-ar mb-3">{mf.heading || "ACCESS CLEARANCE LEVELS"}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {MEMBERSHIPS.map((m, i) => (
          <Reveal key={m.id || m.key} delay={i * 0.06}>
            <div
              className="p-5"
              style={{ border: `1px solid ${m.accent_color ? (m.accent_color + "aa") : "rgba(184,138,66,0.25)"}`, background: "linear-gradient(180deg, rgba(20,17,10,0.55) 0%, rgba(10,9,8,0.85) 100%)" }}
              data-testid={`membership-${i}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.4em] text-[#B88A42] font-mono-ar">{m.key}</div>
                {m.badge ? (
                  <span
                    className="text-[9px] tracking-[0.3em] font-mono-ar px-2 py-0.5"
                    style={{
                      border: "1px solid rgba(184,138,66,0.55)",
                      color: "#E8D08A",
                      background: "rgba(184,138,66,0.12)",
                    }}
                  >{m.badge}</span>
                ) : null}
              </div>
              <div className="text-white font-cairo text-lg font-black mb-2">{m.ar}</div>
              <div className="text-zinc-400 font-cairo text-[12px] leading-[1.9]">{m.note}</div>
              {Array.isArray(m.features) && m.features.length > 0 && (
                <ul className="mt-3 space-y-1 text-[12px] font-cairo text-zinc-300">
                  {m.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-2">
                      <span className="text-[#E8D08A] mt-[6px] w-1 h-1 rounded-full bg-current inline-block" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
              {m.button_text ? (
                <a
                  href={m.button_link || "#"}
                  className="mt-4 inline-flex items-center gap-2 text-[11px] font-cairo tracking-[0.3em] px-3 py-1.5"
                  style={{
                    color: "#E8D08A",
                    border: "1px solid rgba(184,138,66,0.55)",
                    background: "linear-gradient(180deg, rgba(184,138,66,0.10) 0%, rgba(184,138,66,0.04) 100%)",
                  }}
                >{m.button_text}</a>
              ) : null}
              <div className="mt-4 text-[10px] font-mono-ar tracking-[0.3em] text-zinc-500">
                {m.status || (i === 0 ? "متاح" : "قريباً")}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════
//  14) LEADERBOARD (public-safe)
// ══════════════════════════════════════════════════════════════
function AvatarCircle({ name, agent_id, size = 48, tier = 0 }) {
  const initials = (name || `A${agent_id || "?"}`).trim().slice(0, 2);
  const ring = ["#B88A42", "#D6A653", "#E8D08A"][tier] || "#B88A42";
  return (
    <div
      className="relative rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
      style={{
        width: size, height: size,
        border: `1px solid ${ring}80`,
        background: "radial-gradient(circle at 30% 30%, #2a2317 0%, #0e0c08 100%)",
        boxShadow: `inset 0 0 12px rgba(0,0,0,0.55), 0 0 0 2px rgba(0,0,0,0.6), 0 0 0 3px ${ring}30`,
      }}
      aria-hidden
    >
      <Fingerprint size={size * 0.7} className="absolute text-[#B88A42]/15" strokeWidth={0.6} />
      <span
        className="relative font-cairo font-black"
        style={{
          color: "#E8D08A",
          fontSize: size * 0.36,
          textShadow: "0 1px 0 rgba(0,0,0,0.85)",
          letterSpacing: "0.05em",
        }}
      >
        {initials}
      </span>
    </div>
  );
}

export function Leaderboard({ rows, header }) {
  const hf = (header && header.fields) || {};
  const top = (rows || []).slice(0, 3);
  const rest = (rows || []).slice(3, 10);
  return (
    <Section
      id="leaderboard"
      chapter={hf.chapter || "08"}
      eyebrow={hf.eyebrow || "AGENCY RANKING BOARD"}
      title={hf.title || "سجل المحققين"}
      subtitle={hf.subtitle || "المحققون المتقدمون في المنصة. تُعرَض المعلومات العامة فقط — لا يظهر أي حساب TikTok."}
    >
      {top.length === 0 ? (
        <div className="p-6 text-center text-zinc-500 font-cairo text-sm" style={{ border: "1px dashed rgba(184,138,66,0.25)" }}>
          {hf.empty_message || "لا توجد بيانات متاحة بعد. كن أول من يفتح قضية!"}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {top.map((r, i) => (
              <div
                key={r.user_id}
                className="p-5 relative overflow-hidden text-center"
                style={{
                  border: `1px solid ${i === 0 ? "rgba(232,208,138,0.55)" : "rgba(184,138,66,0.28)"}`,
                  background: `linear-gradient(180deg, rgba(${i === 0 ? "40,30,15" : "20,17,10"},0.75) 0%, rgba(10,9,8,0.85) 100%)`,
                }}
              >
                <div className="text-[10px] tracking-[0.5em] text-[#B88A42] font-mono-ar mb-3">POSITION · 0{i + 1}</div>
                <div className="flex justify-center mb-3">
                  <AvatarCircle name={r.name} agent_id={r.agent_id} size={72} tier={i === 0 ? 2 : i === 1 ? 1 : 0} />
                </div>
                <div className="text-white font-cairo text-xl font-black mb-1">{r.name || `Agent #${r.agent_id}`}</div>
                <div className="text-zinc-400 font-cairo text-[12px] mb-3">#{r.agent_id} · {r.rank?.name || "—"}</div>
                <div className="grid grid-cols-3 gap-2 text-[10px] font-cairo">
                  <StatMini label="XP" value={r.points ?? 0} />
                  <StatMini label="انتصارات" value={r.wins ?? 0} />
                  <StatMini label="قضايا" value={r.solved_cases ?? 0} />
                </div>
              </div>
            ))}
          </div>
          {rest.length > 0 && (
            <div className="overflow-x-auto divide-y divide-zinc-800/60" style={{ border: "1px solid rgba(184,138,66,0.18)" }}>
              {rest.map((r, i) => (
                <div key={r.user_id} className="grid grid-cols-[46px_44px_1fr_100px_60px_60px] items-center px-4 py-3 text-[13px] font-cairo gap-3 min-w-[500px]" dir="rtl">
                  <span className="text-[10px] tracking-[0.35em] text-zinc-500 font-mono-ar">0{i + 4}</span>
                  <AvatarCircle name={r.name} agent_id={r.agent_id} size={36} />
                  <span className="text-white">{r.name || `Agent #${r.agent_id}`}</span>
                  <span className="text-zinc-400 text-[11px]">#{r.agent_id} · {r.rank?.name || "—"}</span>
                  <span className="text-[#E8D08A] text-[11px] tabular-nums">{r.points ?? 0}</span>
                  <span className="text-zinc-400 text-[11px] tabular-nums">{r.wins ?? 0}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Section>
  );
}
function StatMini({ label, value }) {
  return (
    <div className="p-2" style={{ border: "1px solid rgba(184,138,66,0.22)", background: "rgba(255,255,255,0.02)" }}>
      <div className="text-zinc-500 text-[9px] tracking-widest">{label}</div>
      <div className="text-[#E8D08A] font-mono-ar text-[14px] tabular-nums">{value}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  15) FAQ
// ══════════════════════════════════════════════════════════════
const FAQ = [
  { q: "هل الموقع تابع لأي جهة أمنية؟", a: "لا. OK FBI لعبة تحقيق خيالية للترفيه، وغير مرتبطة بمكتب التحقيقات الفيدرالي (FBI) أو أي جهة رسمية." },
  { q: "كيف يتم تسجيل الدخول؟", a: "بعد اكتمال الإعداد الرسمي، يستخدم تسجيل الدخول عبر TikTok Login Kit. كتابة اسم مستخدم يدوياً غير مقبولة كوسيلة تحقق." },
  { q: "ما هو Agent ID؟", a: "رقم هوية دائم لكل محقق داخل OK FBI. بعض الأرقام محجوزة للإدارة ولا تُمنح للاعبين." },
  { q: "هل هناك اشتراكات مدفوعة الآن؟", a: "لا. لا توجد أي اشتراكات مدفوعة مفعّلة حالياً. أي عرض قادم سيتم توضيح تفاصيله عند تفعيله رسمياً." },
  { q: "هل تُشارَك بياناتي؟", a: "لا نبيع بياناتك ولا نكشف حساب TikTok الخاص بك لأي مستخدم آخر. راجع سياسة الخصوصية للتفاصيل." },
];
export function FAQSection({ content }) {
  const [open, setOpen] = useState(0);
  const f = (content && content.fields) || {};
  const items = (content && Array.isArray(content.items) && content.items.length)
    ? content.items.filter((i) => i.visible !== false).map((it) => ({ id: it.id, q: it.q, a: it.a }))
    : FAQ;
  return (
    <Section
      id="faq"
      chapter={f.chapter || "09"}
      eyebrow={f.eyebrow || "QUERIES"}
      title={f.title || "الأسئلة الشائعة"}
      subtitle={f.subtitle || ""}
    >
      <div className="space-y-3">
        {items.map((fq, i) => (
          <div key={fq.id || i} className="overflow-hidden" style={{ border: "1px solid rgba(184,138,66,0.22)", background: "rgba(20,17,10,0.35)" }}>
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              className="w-full text-right px-5 py-4 flex items-center justify-between font-cairo text-white text-[14px]"
              data-testid={`faq-${i}`}
              dir="rtl"
            >
              <span>{fq.q}</span>
              <ChevronDown size={14} className={`transition-transform ${open === i ? "rotate-180" : ""} text-[#B88A42]`} />
            </button>
            {open === i && (
              <div className="px-5 pb-5 pt-0 text-zinc-400 font-cairo text-[13px] leading-[1.9]" dir="rtl">{fq.a}</div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════
//  16) FINAL CINEMATIC CTA — corridor
// ══════════════════════════════════════════════════════════════
export function FinalCTA({ authed }) {
  const nav = useNavigate();
  const reduced = useReducedMotion();
  return (
    <section
      id="enter"
      className="relative py-32 overflow-hidden"
      style={{
        background:
          "radial-gradient(60% 90% at 50% 100%, rgba(184,138,66,0.14) 0%, rgba(0,0,0,0) 60%), linear-gradient(180deg, #060605 0%, #0a0908 100%)",
      }}
      data-testid="final-cta"
    >
      {/* Corridor perspective */}
      <div className="absolute inset-0 flex items-end justify-center pointer-events-none" aria-hidden>
        <div
          className="w-[86vw] max-w-[900px] h-[540px] relative"
          style={{ perspective: "800px" }}
        >
          <div
            className="absolute inset-0"
            style={{
              transform: "rotateX(58deg) translateZ(-40px)",
              background:
                "linear-gradient(180deg, transparent 0%, rgba(184,138,66,0.10) 60%, rgba(232,208,138,0.20) 100%)",
              borderTop: "1px solid rgba(184,138,66,0.35)",
              boxShadow: "inset 0 -80px 120px rgba(184,138,66,0.12)",
            }}
          />
          {/* Vertical stripes */}
          <div
            className="absolute inset-0 flex justify-between"
            style={{
              transform: "rotateX(58deg) translateZ(-40px)",
              padding: "0 5%",
            }}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="w-px h-full bg-[#B88A42]/25" />
            ))}
          </div>
          {!reduced && (
            <motion.div
              initial={{ opacity: 0.25 }}
              animate={{ opacity: [0.15, 0.45, 0.15] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-x-0 bottom-0 h-[220px] mx-auto w-[40%]"
              style={{
                background:
                  "radial-gradient(50% 100% at 50% 100%, rgba(232,208,138,0.55) 0%, rgba(184,138,66,0) 70%)",
                filter: "blur(6px)",
              }}
            />
          )}
        </div>
      </div>

      <div className="relative z-10 max-w-[900px] mx-auto px-5 sm:px-8 text-center" dir="rtl">
        <Reveal>
          <div className="text-[10px] tracking-[0.5em] text-[#B88A42] font-mono-ar mb-4">NEXT · CASE</div>
          <h2 className="font-cairo font-black text-white leading-[1.2] mb-4"
              style={{ fontSize: "clamp(1.7rem, 3.5vw, 2.6rem)" }}>
            القضية التالية تنتظر محقّقها.
          </h2>
          <p className="text-zinc-400 font-cairo text-base sm:text-lg mb-8 max-w-[620px] mx-auto leading-[2]">
            هل ستكون أنت من يكشف الحقيقة؟
          </p>
          <PrimaryButton onClick={() => nav(authed ? "/app" : "/login")} data-testid="final-cta-button">
            دخول مقر التحقيق
          </PrimaryButton>
        </Reveal>
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════
//  ABOUT SECTION — cinematic 3-column feature grid
// ══════════════════════════════════════════════════════════════
export function AboutSection({ content }) {
  const f = (content && content.fields) || {};
  const items = (content && Array.isArray(content.items) && content.items.length)
    ? content.items.filter((i) => i.visible !== false)
    : [
        { title: "أدلة تفاعلية", description: "بصمات، محادثات، كاميرات ووثائق موثّقة داخل ملف القضية.", icon: "Fingerprint", cover_index: 0 },
        { title: "خيوط متشابكة", description: "اربط بين المشتبه به، المكان، الوقت والدافع.",              icon: "Radar",       cover_index: 2 },
        { title: "نظرية شخصية", description: "احفظ رأيك قبل الكشف النهائي، وقارنه بالحقيقة.",             icon: "FileText",    cover_index: 4 },
      ];
  const iconMap = { Fingerprint, Radar, FileText };
  return (
    <Section
      id="about"
      chapter={f.chapter || "01"}
      eyebrow={f.eyebrow || "ABOUT"}
      title={f.title || "داخل مقر تحقيق سري"}
      subtitle={f.subtitle || "OK FBI تجربة تحقيق سينمائية تفاعلية. لا مصادفات في الأدلة، ولا شيء يظهر بلا سبب."}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((it, i) => {
          const Icon = iconMap[it.icon] || FileText;
          const cover = it.cover_url && it.cover_url.trim() !== ""
            ? it.cover_url
            : publicMedia(Number.isFinite(it.cover_index) ? it.cover_index : i * 2);
          return (
            <Reveal key={it.id || it.title || i} delay={i * 0.08}>
              <div className="relative overflow-hidden h-full group"
                   style={{ border: `1px solid ${it.accent_color ? (it.accent_color + "aa") : "rgba(184,138,66,0.22)"}`, background: "#0a0908" }}>
                <div
                  className="absolute inset-0 transition-transform duration-700 group-hover:scale-[1.04]"
                  style={{
                    backgroundImage: `url(${cover})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "grayscale(45%) contrast(1.05) brightness(0.5) saturate(0.85)",
                  }}
                  aria-hidden
                />
                <div className="absolute inset-0"
                     style={{ background: "linear-gradient(180deg, rgba(6,6,5,0.55) 0%, rgba(6,6,5,0.95) 90%)" }} aria-hidden />
                <div className="relative p-6 h-full flex flex-col">
                  <Icon size={22} className="mb-3" strokeWidth={1.3} style={{ color: it.accent_color || "#E8D08A" }} />
                  <div className="text-white font-cairo text-lg font-black mb-2">{it.title}</div>
                  <div className="text-zinc-300 font-cairo text-[13px] leading-[1.9]">{it.description}</div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
}

// ══════════════════════════════════════════════════════════════
//  DISCLAIMER — fictional-content disclosure strip (shared)
// ══════════════════════════════════════════════════════════════
export function FictionDisclaimer() {
  return (
    <div className="max-w-[1080px] mx-auto px-5 sm:px-8 pt-10 pb-6 text-[10px] leading-relaxed text-zinc-500 font-cairo tracking-wider text-center" data-testid="home-fiction-disclaimer" dir="rtl">
      OK FBI لعبة تحقيق تفاعلية خيالية لأغراض الترفيه. جميع القضايا والشخصيات والأسماء والأحداث والأدلة داخل اللعبة من وحي الخيال، وأي تشابه مع أشخاص أو أحداث حقيقية هو محض مصادفة. OK FBI ليست جهة حكومية أو أمنية ولا ترتبط أو تتبع مكتب التحقيقات الفيدرالي (FBI) أو أي جهة رسمية.
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  17) DATA HOOK + LAYOUT (shared across public pages)
// ══════════════════════════════════════════════════════════════

/** Shared data hook used by every public page — fetches ranks, leaderboard,
 *  and CMS config exactly once per mount. */
export function usePublicData(opts = {}) {
  const fetchRanks = !!opts?.fetchRanks;
  const fetchLeaderboard = !!opts?.fetchLeaderboard;
  const { user } = useAuth() || {};
  const authed = !!user;
  const isOwner = user?.role === "OWNER";
  const [ranks, setRanks] = useState([]);
  const [rows, setRows] = useState([]);
  const [cms, setCms] = useState(null);
  const { pages, isPreview } = usePageContent();
  useEffect(() => {
    if (fetchRanks) {
      api.get("/ranks").then(({ data }) => setRanks(data?.ranks || [])).catch(() => setRanks([]));
    }
    if (fetchLeaderboard) {
      api.get("/leaderboard").then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
    }
    api.get("/homepage/config").then(({ data }) => setCms(data || null)).catch(() => setCms(null));
  }, [fetchRanks, fetchLeaderboard]);
  return { user, authed, isOwner, ranks, rows, cms, pages, isPreview };
}

/** Public shell layout — header + atmospheric layers + body + footer. */
export function PublicLayout({ title, description, children, showIntro = false }) {
  const { authed, isOwner } = usePublicData();
  const [showOpen, setShowOpen] = useState(false);
  // Slice V1: no-op unless `?edit=1` is present in the URL. Safe for visitors.
  useEditorBridge();
  useEffect(() => {
    try {
      if (showIntro) {
        const seen = sessionStorage.getItem("okfbi_intro_seen") === "1";
        setShowOpen(!seen);
      }
    } catch { if (showIntro) setShowOpen(true); }
    try {
      if (title) document.title = title;
      if (description) {
        const meta = document.querySelector("meta[name='description']");
        if (meta) meta.setAttribute("content", description);
      }
      // Every route renders from the top — never keep scroll position across
      // real page transitions.
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    } catch {}
  }, [title, description, showIntro]);
  const doneIntro = () => {
    try { sessionStorage.setItem("okfbi_intro_seen", "1"); } catch {}
    setShowOpen(false);
  };
  return (
    <div
      dir="rtl"
      className="relative w-full min-h-screen text-white overflow-x-hidden"
      style={{ background: "#050505" }}
      onContextMenu={(e) => {
        if (e.target && (e.target.tagName === "IMG" || e.target.tagName === "svg")) e.preventDefault();
      }}
      onDragStart={(e) => {
        if (e.target && e.target.tagName === "IMG") e.preventDefault();
      }}
      data-testid="public-layout"
    >
      <FilmGrain />
      <Dust />
      {showOpen && <OpeningSequence onDone={doneIntro} />}
      <PublicHeader authed={authed} isOwner={isOwner} />
      <div aria-hidden className="h-[68px]" />
      {children}
      <PublicFooter />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  18) HOMEPAGE — hero + final CTA only (strict isolation)
// ══════════════════════════════════════════════════════════════
export default function PublicHomePage() {
  const { authed, cms, pages } = usePublicData();
  const rawHome = pages?.home?.sections || [];

  // Explicit blocklist: legacy composite types must NEVER render on the homepage (/),
  // even if configured in CMS home.sections or returned by any renderer.
  // They live exclusively on their dedicated routes (/case-preview, /leaderboard-public, /how-to-play, /faq, /about).
  const EXCLUDED_HOME_TYPES = new Set([
    "case_preview",
    "leaderboard",
    "leaderboard_header",
    "how_it_works",
    "faq",
    "about",
    "connect_dots",
    "theory_preview",
    "ranks",
    "agent_id_showcase",
  ]);

  // Strip out any excluded types from CMS home sections immediately
  const home = rawHome.filter((s) => s && !EXCLUDED_HOME_TYPES.has(s.type));

  const PrimitiveBlockList = _PrimitiveBlockList;

  const CANONICAL_ORDER = [
    "hero",
    "final_cta",
  ];

  const normalizeType = (t) => {
    if (t === "cta") return "final_cta";
    return t;
  };

  const getSectionData = (normType) => {
    if (EXCLUDED_HOME_TYPES.has(normType)) return null;
    const inHome = home.find(
      (s) =>
        s.type === normType ||
        (normType === "final_cta" && s.type === "cta")
    );
    return inHome || null;
  };

  // Reordering: respect order of recognized composite sections in home.sections
  const orderInHome = [];
  home.forEach((s) => {
    const norm = normalizeType(s.type);
    if (CANONICAL_ORDER.includes(norm) && !orderInHome.includes(norm)) {
      orderInHome.push(norm);
    }
  });

  const finalOrder = [
    ...orderInHome,
    ...CANONICAL_ORDER.filter((t) => !orderInHome.includes(t)),
  ];

  // Render ONLY hero and final_cta; all excluded types are strictly blocked
  const visibleSections = finalOrder
    .map((type) => {
      if (EXCLUDED_HOME_TYPES.has(type)) return null;
      const sec = getSectionData(type);
      if (sec && sec.visible === false) return null;
      return { type, sec };
    })
    .filter(Boolean);

  const renderSection = ({ type, sec }, index) => {
    if (EXCLUDED_HOME_TYPES.has(type)) return null;
    const isFirst = index === 0;
    const isHero = type === "hero";
    const wrapperCls = isFirst && isHero ? "-mt-[68px]" : undefined;

    switch (type) {
      case "hero":
        return (
          <div key="hero" data-block-id={sec?.id} data-block-type="hero" className={wrapperCls}>
            <Hero authed={authed} cms={cms} content={sec} />
          </div>
        );
      case "final_cta":
        return (
          <div key="final_cta" data-block-id={sec?.id} data-block-type="final_cta" className={wrapperCls}>
            <FinalCTA authed={authed} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <PublicLayout
      title="OK FBI | لعبة تحقيق تفاعلية"
      description="تجربة تحقيق تفاعلية خيالية تعتمد على تحليل الأدلة، بناء النظريات، التصويت والتنافس بين المحققين."
      showIntro
    >
      <div data-testid="public-home">
        {visibleSections.map(renderSection)}
        <PrimitiveBlockList sections={home} />
      </div>
    </PublicLayout>
  );
}
